"use client";

import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Stack,
  IconButton,
} from "@mui/material";
import ServiceQuoteForm from "../../proposal/service-quote-form/ServiceQuoteForm";
import patternImage from "../assets/patternBackground.png";
import SocialIcon from "../../configurations/footer-manager/components/SociaIcon";
// Import your complete pill selection & quote component

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const socialPlatforms = [
    {
      name: "Facebook",
      color: "#1877F2",
      url: "https://www.facebook.com/mymuzukuru/",
    },
    {
      name: "Instagram",
      color: "#E4405F",
      url: "https://www.instagram.com/muzukurufuneralstreaming/",
    },
    {
      name: "LinkedIn",
      color: "#0A66C2",
      url: "https://linkedin.com/company/muzukuru",
    },
    {
      name: "YouTube",
      color: "#FF0000",
      url: "https://www.youtube.com/@Muzukuru",
    },
  ];

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Shared styles for standard text fields to mimic the clean underline look
  const textInputStyles = {
    "& .MuiInput-underline:before": {
      borderColor: "rgba(196, 184, 168, 0.4)", // Subtle underline matching image
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderColor: "#f5f0e8",
    },
    "& .MuiInput-underline:after": {
      borderColor: "#f5f0e8", // Focused line color
    },
    "& .MuiInputBase-input": {
      color: "#f5f0e8",
      fontSize: "0.9rem",
      pb: 1.5,
      "&::placeholder": {
        color: "#c4b8a8",
        opacity: 0.8,
      },
    },
  };

  return (
    <Box
      sx={{
        backgroundColor: "#5E3E2B",
        backgroundImage: `url(${patternImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        py: { xs: 2, md: 4 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "auto",
      }}
    >
      <Container maxWidth="md">
        {/* Header Section */}
        <Box textAlign="center" mb={4}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 400,
              color: "#f5f0e8",
              mb: 1.5,
              fontSize: { xs: "1.75rem", md: "2.3rem" },
              letterSpacing: "0.5px",
            }}
          >
            Get In Touch With Us
          </Typography>
          <Typography
            sx={{
              color: "#c4b8a8",
              fontSize: "0.85rem",
              opacity: 0.9,
            }}
          >
            Create a quick inquiry in a few clicks!
          </Typography>
        </Box>

        {/* Entire Quick-Select Form Pill Component Container */}
        <Box
          sx={{
            transform: {
              xs: "scale(0.92)",
              md: "scale(0.85)",
            },
            transformOrigin: "top center",
            mb: { xs: 2, md: "-50px" },

            // Change background color
            "& > div > div:first-of-type": {
              backgroundColor: "#5E3E2B",
            },

            "& *": {
              color: "white",
            },
          }}
        >
          <ServiceQuoteForm />
        </Box>

        {/* Main Card Container */}
        <Box
          sx={{
            mt: 7,
            backgroundColor: "#825639", // Tinted background container wrapper
            backdropFilter: "blur(4px)",
            borderRadius: "24px",
            p: { xs: 3, md: 4 },
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 4,
          }}
        >
          {/* Left Side: Contact Info Block */}
          <Box
            sx={{
              flex: 1,
              backgroundColor: "#5E3E2B", // The precise dark block inner card
              borderRadius: "20px",
              p: 4,
            }}
          >
            <Typography
              sx={{
                color: "#f5f0e8",
                fontWeight: 600,
                mb: 4,
                fontSize: "1.05rem",
              }}
            >
              Our Contacts
            </Typography>

            {/* Zimbabwe Branch */}
            <Box mb={4}>
              <Typography
                sx={{
                  color: "#c4b8a8",
                  fontSize: "0.8rem",
                  opacity: 0.7,
                  mb: 1,
                }}
              >
                Zimbabwe Branch
              </Typography>
              <Typography
                sx={{ color: "#f5f0e8", fontSize: "0.85rem", mb: 0.5 }}
              >
                +263 776 402 402
              </Typography>
              <Typography
                sx={{ color: "#f5f0e8", fontSize: "0.85rem", mb: 0.5 }}
              >
                sales@muzukuru.co.zw
              </Typography>
              <Typography sx={{ color: "#c4b8a8", fontSize: "0.85rem" }}>
                46B Mt. Pleasant Drive, Harare
              </Typography>
            </Box>

            {/* Social Media Circles */}
            <Box>
              <Typography
                sx={{
                  color: "#c4b8a8",
                  fontSize: "0.8rem",
                  opacity: 0.7,
                  mb: 1.5,
                }}
              >
                Social Media
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {socialPlatforms.map((platform) => (
                  <IconButton
                    key={platform.name}
                    component="a"
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      backgroundColor: "#543d2b",
                      width: 38,
                      height: 38,
                      "&:hover": {
                        backgroundColor: "#664c38",
                      },
                    }}
                  >
                    <SocialIcon
                      name={platform.name}
                      sx={{
                        fontSize: 18,
                        color: platform.color,
                      }}
                    />
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* Right Side: Contact Form Fields */}
          <Box
            sx={{
              flex: 1.6,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              py: 1,
            }}
          >
            <Stack spacing={4}>
              <TextField
                variant="standard"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
                fullWidth
                sx={textInputStyles}
              />
              <TextField
                variant="standard"
                name="phone"
                placeholder="Your Phone Number"
                value={formData.phone}
                onChange={handleInputChange}
                fullWidth
                sx={textInputStyles}
              />
              <TextField
                variant="standard"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleInputChange}
                fullWidth
                sx={textInputStyles}
              />
              <TextField
                variant="standard"
                name="message"
                placeholder="Your Message"
                value={formData.message}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
                sx={textInputStyles}
              />
            </Stack>

            {/* Submit Button Block */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
              <IconButton
                disableRipple
                sx={{
                  backgroundColor: "#f5f0e8",
                  color: "#422e20",
                  borderRadius: "8px",
                  px: 3,
                  py: 1,
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#e6ded0",
                  },
                }}
              >
                Send Message
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
